<template>
  <PdfRedactingPanelWrapper data-locator="redact-by-keywords-card">
    <template #title>
      <h3 class="text-text-primary">
        {{ $t('common.PdfRedactingSidebar.textSearch') }}
      </h3>
    </template>

    <template #content>
      <SearchTextField
        v-model.trim="searchQuery"
        class="mb-2"
        :disabled="isSearchDisabled"
        :placeholder="$t('common.PdfRedactingSidebar.enterTextToRedact')"
        :hint="$t('common.PdfRedactingSidebar.textSearchHint')"
        :location="'top'"
        fluid
        @keydown.enter="searchOccurrences(searchQuery, true, SEARCH_TYPES.MANUAL)"
      />

      <v-skeleton-loader v-if="isLoading" class="my-3" type="card"></v-skeleton-loader>
      <template v-else>
        <!-- List of results -->
        <v-list v-if="manualSearchResults.length">
          <v-list-item
            v-for="(result, index) in manualSearchResults"
            :key="index"
          >
            <div class="d-flex align-center justify-space-between">
              <!-- Checkbox -->
              <v-checkbox
                v-model="result.selected"
                :label="`${result.query} (${result.count})`"
                hide-details
                density="compact"
                @update:modelValue="updateRedactedZones"
              />
              <!-- Delete button -->
              <DeletePopover
                v-if="isDocumentProcessingComplete"
                :item="result"
                @confirm="removeResult(result)"
              />
            </div>
          </v-list-item>
        </v-list>
        <!-- No occurrences found -->
        <p
          v-else-if="isDocumentProcessingComplete && !foundOccurrences"
          class="text-center"
        >
          {{ $t('common.PdfRedactingSidebar.noResults') }}
        </p>
        <!-- Error -->
        <p
          v-else-if="hasError"
          class="text-text-danger text-center"
        >
          {{ $t('common.PdfRedactingSidebar.searchError') }}
        </p>
      </template>
    </template>
  </PdfRedactingPanelWrapper>
</template>

<script>
import DeletePopover from "@/common/popover/DeletePopover.vue"
import SearchTextField from '@/common/SearchTextField.vue'

import PdfRedactingPanelWrapper from './PdfRedactingPanelWrapper.vue'
import { SEARCH_TYPES } from '../constants'
import { convertMatches, renderMatches, getOriginalIndex, match, normalize } from '../pdf-document-search.js'

export default {
  name: 'PdfRedactingSidebarContentKeyWords',
  components: {
    PdfRedactingPanelWrapper,
    SearchTextField,
    DeletePopover,
  },
  emits: ['updateRectangles'],
  props: {
    pdfAppInstance: {
      required: true,
      type: Object,
    },
    pdfHasText: {
      required: true,
      type: Boolean,
    },
  },
  data() {
    return this.getInitialData()
  },
  watch: {
    isDocumentProcessingComplete(value) {
      if (value) {
        if (this.selectedItems.length > 0) {
          this.selectedItems.slice(1).forEach(item => {
            if (item.query) {
              this.searchOccurrences(item.query)
              this.previouslySearchedItems.add(item.query) // Mark item as searched
            }
          })
        }
        this.isLoading = false
      }
    },
  },
  computed: {
    manualSearchResults() {
      return this.searchResults.filter(result => result.type === SEARCH_TYPES.MANUAL)
    },
    totalPages() {
      return this.pdfAppInstance?.pdfDocument?.numPages || 0
    },
    isDocumentProcessingComplete() {
      return this.processedPages === this.totalPages
    },
    isSearchDisabled() {
      return (!this.isDocumentProcessingComplete
          && (this.isLoading || this.manualSearchResults.length > 0)
        )
        || !this.pdfHasText
    },
  },
  methods: {
    async searchOccurrences (query = this.searchQuery, selected = false, type = SEARCH_TYPES.AI) {
      if (!query || !this.pdfAppInstance?.pdfDocument) return

      if (this.previouslySearchedItems.has(query)) {
        return
      }

      const [lowerQuery] = normalize(query)
      if (this.searchResults.some(result => normalize(result.query)[0] === lowerQuery)) {
        this.searchQuery = ''
        this.updatePendingSearches()
        return
      }

      this.isLoading = true
      this.processedPages = 0
      this.foundOccurrences = false
      this.hasError = false
      let occurrences = []
      let count = 0

      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        const page = await this.pdfAppInstance.pdfDocument.getPage(pageNum)
        const textContent = await page.getTextContent({ disableNormalization: true })
        const strBuf = [];

        for (const textItem of textContent.items) {
          strBuf.push(textItem.str);
          if (textItem.hasEOL) {
            strBuf.push("\n");
          }
        }

        if (this.pageContents[pageNum - 1] === undefined) {
          [
            this.pageContents[pageNum - 1],
            this.pageDiffs[pageNum - 1],
            this.hasDiacritics[pageNum - 1],
          ] = normalize(strBuf.join(""));
        }

        const matcherResult = match(lowerQuery, this.pageContents[pageNum - 1], this.hasDiacritics[pageNum - 1])

        const matches = []
        const matchesLength = []
        const diffs = this.pageDiffs[pageNum - 1];

        matcherResult?.forEach(({ index, length }) => {
          const [matchPos, matchLen] = getOriginalIndex(diffs, index, length);
          this.foundOccurrences = true
          if (matchLen) {
            matches.push(matchPos);
            matchesLength.push(matchLen);
          }
        });

        if (matches.length === 0) {
          this.processedPages++
          continue
        }

        count += matches.length

        const pageElement = this.pdfAppInstance.pdfViewer.container.querySelector(`.page[data-page-number="${pageNum}"]`)
        let textLayer = pageElement.querySelector('.textLayer')

        if (textLayer === null) {

          let pageView = this.pdfAppInstance.pdfViewer.getPageView(pageNum - 1)
          pageView.generateTextLayer()
          await pageView.renderTextLayer()

          textLayer = pageView.textLayer.div
        }

        let pageView = this.pdfAppInstance.pdfViewer.getPageView(pageNum - 1)

        const {
          textDivs,
          textContentItemsStr,
        } = pageView.textLayer.highlighter

        const convertedMatches = convertMatches(matches, matchesLength, textContentItemsStr)

        const rectangles = renderMatches(convertedMatches, textDivs, textLayer, pageNum)

        occurrences.push(...rectangles)

        if (!this.isDocumentProcessingComplete) {
          this.processedPages++
        }
      }

      this.updateSearchResults(query, count, occurrences, selected, type)
      this.updatePendingSearches()
      if (type === SEARCH_TYPES.MANUAL) {
        this.searchQuery = ''
      }
    },
    updatePendingSearches() {
      if (this.pendingSearches === 0) return
      this.pendingSearches--
      if (this.pendingSearches === 0) {
        this.isLoading = false
      }
    },
    updateSearchResults(query, count, occurrences, selected, type) {
      const existingIndex = this.searchResults.findIndex(result => result.query === query)
      if (existingIndex !== -1) {
        this.searchResults[existingIndex].count = count
        this.searchResults[existingIndex].occurrences = occurrences
      } else {
        this.searchResults.push({
          selected,
          query,
          count,
          occurrences,
          type,
        })
      }
      this.updateRedactedZones()
    },
    removeResult(result) {
      const index = this.searchResults.indexOf(result)
      if (index > -1) {
        this.searchResults.splice(index, 1)
        this.updateRedactedZones()
      }
    },
    updateRedactedZones() {
      const updatedOccurrences = this.searchResults.flatMap(item =>
        item.selected ? item.occurrences : [],
      )

      this.$emit('updateRectangles', updatedOccurrences)
    },
    getInitialData() {
      return {
        searchQuery: '',
        isLoading: false,
        pendingSearches: 0,
        searchResults: [],
        SEARCH_TYPES,
        processedPages: 0,
        foundOccurrences: false,
        hasError: false,
        selectedItems: [],
        previouslySearchedItems: new Set(),
        pageContents: [], // Stores the normalized text for each page
        pageDiffs: [], // Stores diffs between original and normalized text for each page
        hasDiacritics: [], // Store the info that a page contains diacritics glyphs
      }
    },
    resetData() {
      Object.assign(this.$data, this.getInitialData())
    },
  },
}
</script>
